import { z } from 'zod';

/**
 * Every API error response uses this envelope (per MASTER_PROMPT.md §9: "consistent error
 * format"). `code` is a stable machine-readable string clients can branch on; `message` must
 * state what actually happened and, where applicable, what to try - never a bare "failed".
 */
export const errorResponseSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    requestId: z.string().optional(),
    details: z.record(z.unknown()).optional(),
  }),
});

export type ErrorResponseBody = z.infer<typeof errorResponseSchema>;

export const ERROR_CODES = {
  VALIDATION_FAILED: 'validation_failed',
  NOT_FOUND: 'not_found',
  UNAUTHENTICATED: 'unauthenticated',
  FORBIDDEN: 'forbidden',
  TENANT_MISMATCH: 'tenant_mismatch',
  IDEMPOTENCY_CONFLICT: 'idempotency_conflict',
  RATE_LIMITED: 'rate_limited',
  CONFLICT: 'conflict',
  INVENTORY_UNAVAILABLE: 'inventory_unavailable',
  PAYMENT_FAILED: 'payment_failed',
  PROVIDER_ERROR: 'provider_error',
  INTERNAL_ERROR: 'internal_error',
} as const;

export type ErrorCode = (typeof ERROR_CODES)[keyof typeof ERROR_CODES];

const STATUS_BY_CODE: Record<ErrorCode, number> = {
  validation_failed: 400,
  not_found: 404,
  unauthenticated: 401,
  forbidden: 403,
  tenant_mismatch: 403,
  idempotency_conflict: 409,
  rate_limited: 429,
  conflict: 409,
  inventory_unavailable: 409,
  payment_failed: 402,
  provider_error: 502,
  internal_error: 500,
};

export class AppError extends Error {
  readonly code: ErrorCode;
  readonly httpStatus: number;
  readonly details?: Record<string, unknown>;

  constructor(code: ErrorCode, message: string, details?: Record<string, unknown>) {
    super(message);
    this.name = 'AppError';
    this.code = code;
    this.httpStatus = STATUS_BY_CODE[code];
    this.details = details;
  }

  toResponseBody(requestId?: string): ErrorResponseBody {
    return {
      error: {
        code: this.code,
        message: this.message,
        requestId,
        details: this.details,
      },
    };
  }
}

export class NotFoundError extends AppError {
  constructor(resource: string, id: string) {
    super(ERROR_CODES.NOT_FOUND, `${resource} "${id}" was not found.`, { resource, id });
  }
}

export class ForbiddenError extends AppError {
  constructor(message: string, details?: Record<string, unknown>) {
    super(ERROR_CODES.FORBIDDEN, message, details);
  }
}

export class TenantMismatchError extends AppError {
  constructor(resource: string, id: string) {
    super(
      ERROR_CODES.TENANT_MISMATCH,
      `${resource} "${id}" does not belong to the authenticated tenant.`,
      { resource, id },
    );
  }
}

export class InventoryUnavailableError extends AppError {
  constructor(ticketTypeId: string, requested: number, available: number) {
    super(
      ERROR_CODES.INVENTORY_UNAVAILABLE,
      `Requested ${requested} ticket(s) of type "${ticketTypeId}" but only ${available} remain. Reduce the quantity or choose another ticket type.`,
      { ticketTypeId, requested, available },
    );
  }
}

export class IdempotencyConflictError extends AppError {
  constructor(key: string) {
    super(
      ERROR_CODES.IDEMPOTENCY_CONFLICT,
      `Idempotency key "${key}" was already used with a different request body.`,
      { key },
    );
  }
}
